import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ChevronDown, ChevronUp, BookOpen, Heart, Cross, Church, ArrowLeft, ExternalLink } from 'lucide-react';
import { AutoTranslate } from '../hooks/useAutoTranslate';

const KristnaLaranPage = () => {
  const { t, isRTL } = useLanguage();
  const [openSections, setOpenSections] = useState({});
  const [activeTab, setActiveTab] = useState('intro');

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Prayers data
  const prayers = {
    korstecknet: {
      title: 'Korstecknet',
      content: 'Korstecknet görs med högra handen från pannan till bröstet och från vänstra axeln till den högra. Det är en påminnelse om dopet i den Treenige Gudens namn, en bekännelse till Kristi kors, en bön om hans beskydd och ett uttryck för vår vilja att tjäna honom med förstånd och känsla, själ och kropp.',
      prayer: 'I Faderns ✝ och Sonens och den helige Andes namn. Amen.'
    },
    forlåtelse: {
      title: 'Bön om förlåtelse',
      content: 'Jag bekänner inför Gud allsmäktig och er alla att jag har syndat i tankar och ord, gärningar och underlåtelse (man slår sig för bröstet). Detta är min skuld, min stora skuld. Därför ber jag den saliga jungfrun Maria, Guds änglar och helgon och er alla att be för mig till Herren, vår Gud.'
    },
    faderVår: {
      title: 'Herrens bön',
      intro: 'Fader vår är den främsta av alla böner. I den har Jesus visat sina lärjungar att de får tala till Gud, den Oändlige, som barn talar med sin far.',
      content: 'Fader vår, som är i himmelen, helgat varde ditt namn, tillkomme ditt rike, ske din vilja såsom i himmelen så och på jorden. Vårt dagliga bröd giv oss idag, och förlåt oss våra skulder, såsom ock vi förlåta dem oss skyldiga äro, och inled oss icke i frestelse, utan fräls oss ifrån ondo. Ty riket är ditt, och makten, och härligheten, i evighet. Amen.'
    },
    trosbekännelse: {
      title: 'Trosbekännelse',
      content: 'Jag tror på en Gud Fader allsmäktig, himmelens och jordens skapare. Jag tror ock på Jesus Kristus, hans enfödde Son, vår Herre, vilken är avlad av den helige Ande, född av jungfrun Maria, pinad under Pontius Pilatus, korsfäst, död och begraven, nedstigen i dödsriket, på tredje dagen uppstånden igen ifrån de döda, uppstigen till himmelen, sittande på allsmäktig Gud Faders högra sida, därifrån igenkommande till att döma levande och döda. Jag tror ock på den helige Ande, den heliga, katolska kyrkan, de heligas samfund, syndernas förlåtelse, köttets uppståndelse och ett evigt liv.'
    },
    aveMaria: {
      title: 'Ave Maria',
      content: 'Var hälsad Maria, full av nåd, Herren är med dig. Välsignad är du bland kvinnor och välsignad är din livsfrukt Jesus. Heliga Maria Guds moder bed för oss syndare nu och i vår dödsstund. Amen.'
    },
    lovprisning: {
      title: 'Lovprisningen',
      content: 'Ära vare Fadern och Sonen och den helige Ande. Såsom det var av begynnelsen, nu är och skall vara, från evighet till evighet. Amen.'
    }
  };

  // Knowledge sections
  const knowledgeSections = [
    {
      id: 'gudsBud',
      title: 'Tio Guds bud',
      intro: 'Jag är Herren, din Gud:',
      items: [
        'Du ska inga andra gudar ha, vid sidan om mig.',
        'Du ska inte missbruka Herrens, din Guds namn.',
        'Tänk på vilodagen, så att du helgar den.',
        'Visa aktning för din far och din mor.',
        'Du ska inte dräpa.',
        'Du ska inte bryta ett äktenskap.',
        'Du ska inte stjäla.',
        'Du ska inte ljuga.',
        'Du ska inte ha begärelse till din nästas hustru.',
        'Du ska inte ha begärelse till din nästas egendom.'
      ]
    },
    {
      id: 'kyrkansBud',
      title: 'Kyrkans bud',
      items: [
        'Delta i mässan på söndagar och andra förpliktande helgdagar.',
        'Bekänna sina synder i botens sakrament åtminstone en gång om året.',
        'Ta emot eukaristins sakrament åtminstone under påsktiden.',
        'Avstå från kött och fasta på de dagar som är föreskrivna av kyrkan.',
        'Bidra efter förmåga till kyrkans materiella behov.'
      ]
    },
    {
      id: 'sakramenten',
      title: 'Sakramenten',
      intro: 'Sakramenten är det mest grundläggande i katolikernas liv, för med sakramenten får vi gemenskap med Gud, frälsning, förlåtelse, helande, kraft att tro, hoppas och älska; kort sagt den nödvändiga hjälpen att leva som katolska kristna.',
      items: [
        { name: 'Dopet', desc: 'Det första sakramentet som grundlägger det kristna livet och gör oss till medlemmar i kyrkan.' },
        { name: 'Konfirmationen', desc: 'Stärker dopets nåd genom den Helige Andes gåva.' },
        { name: 'Eukaristin', desc: 'Källan och höjdpunkten i det kristna livet - Kristi kropp och blod.' },
        { name: 'Botens sakrament (Bikten)', desc: 'Guds förlåtelse och försoning genom bikten.' },
        { name: 'De sjukas smörjelse', desc: 'Helande och styrka för de sjuka och döende.' },
        { name: 'Vigningens sakrament', desc: 'Prästvigning för tjänst i Kyrkan.' },
        { name: 'Äktenskapets sakrament', desc: 'Förbundet mellan man och kvinna inför Gud.' }
      ],
      link: 'https://www.katolskakyrkan.se/forsamlingsliv/kyrkans-sju-sakrament'
    },
    {
      id: 'teologalaDygder',
      title: 'De tre teologala dygderna',
      items: ['Tro', 'Hopp', 'Kärlek']
    },
    {
      id: 'kardinaldygder',
      title: 'De fyra kardinaldygderna',
      items: ['Klokhet', 'Rättvisa', 'Uthållighet', 'Måttlighet']
    },
    {
      id: 'andensGåvor',
      title: 'De Helige Andens sju gåvor',
      items: ['Vishet', 'Insikt', 'Råd', 'Styrka', 'Förstånd', 'Fromhet', 'Gudsfruktan']
    },
    {
      id: 'huvudsynder',
      title: 'De sju huvudsynderna',
      items: ['Högmod', 'Girighet', 'Lusta', 'Vrede', 'Frosseri', 'Avund', 'Lättja']
    }
  ];

  // Christian doctrine tabs
  const doctrineTabs = [
    { id: 'intro', label: 'Inledning' },
    { id: 'part1', label: 'Första delen' },
    { id: 'part2', label: 'Andra delen' },
    { id: 'part3', label: 'Tredje delen' }
  ];

  return (
    <div className="min-h-screen themed-page">
      <Navbar />

      {/* Header */}
      <section className="relative h-72 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900 to-purple-700" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <Link to="/fordjupning" className={`inline-flex items-center text-purple-200 hover:text-white mb-4 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}>
              <ArrowLeft className={`w-4 h-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
              <AutoTranslate>Tillbaka till Fördjupning</AutoTranslate>
            </Link>
            <h1 className="responsive-hero-title-sm font-serif font-bold text-white mb-4">
              <AutoTranslate>Den Kristna Läran</AutoTranslate>
            </h1>
            <p className="text-xl text-purple-200">
              <AutoTranslate>Böner, bud och sanningar för den kristna tron</AutoTranslate>
            </p>
          </div>
        </div>
      </section>

      {/* Prayers Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center gap-3 mb-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Heart className="w-8 h-8 text-purple-600" />
            <h2 className="text-3xl font-serif font-bold text-gray-900">
              <AutoTranslate>Böner som alla skall kunna</AutoTranslate>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(prayers).map(([key, prayer]) => (
              <Card key={key} className="border-0 shadow-xl card-hover">
                <CardContent className="p-6">
                  <h3 className="text-xl font-serif font-bold text-purple-800 mb-4">
                    <AutoTranslate>{prayer.title}</AutoTranslate>
                  </h3>
                  {prayer.intro && (
                    <p className="text-gray-600 mb-3 italic">
                      <AutoTranslate>{prayer.intro}</AutoTranslate>
                    </p>
                  )}
                  <p className="text-gray-700 leading-relaxed">
                    <AutoTranslate>{prayer.content}</AutoTranslate>
                  </p>
                  {prayer.prayer && (
                    <p className="mt-4 p-3 bg-purple-50 rounded-lg text-purple-800 font-medium">
                      <AutoTranslate>{prayer.prayer}</AutoTranslate>
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Knowledge Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center gap-3 mb-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <BookOpen className="w-8 h-8 text-blue-600" />
            <h2 className="text-3xl font-serif font-bold text-gray-900">
              <AutoTranslate>Det måste du veta</AutoTranslate>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {knowledgeSections.map((section) => (
              <Card key={section.id} className="border-0 shadow-md overflow-hidden">
                <button
                  onClick={() => toggleSection(section.id)}
                  className={`w-full p-4 flex items-center justify-between bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <span className="font-semibold">
                    <AutoTranslate>{section.title}</AutoTranslate>
                  </span>
                  {openSections[section.id] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                {openSections[section.id] && (
                  <CardContent className="p-4">
                    {section.intro && (
                      <p className="text-gray-600 italic mb-3">
                        <AutoTranslate>{section.intro}</AutoTranslate>
                      </p>
                    )}
                    <ol className="space-y-2">
                      {section.items.map((item, idx) => (
                        <li key={`${section.id}-item-${idx}`} className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-sm font-medium">
                            {idx + 1}
                          </span>
                          {typeof item === 'string' ? (
                            <span className="text-gray-700">
                              <AutoTranslate>{item}</AutoTranslate>
                            </span>
                          ) : (
                            <div>
                              <span className="text-gray-900 font-medium">
                                <AutoTranslate>{item.name}</AutoTranslate>
                              </span>
                              {item.desc && (
                                <p className="text-gray-600 text-sm mt-1">
                                  <AutoTranslate>{item.desc}</AutoTranslate>
                                </p>
                              )}
                            </div>
                          )}
                        </li>
                      ))}
                    </ol>
                    {section.link && (
                      <a 
                        href={section.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-4 text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        <AutoTranslate>Läs mer på katolskakyrkan.se</AutoTranslate>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Christian Doctrine Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center gap-3 mb-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Cross className="w-8 h-8 text-green-600" />
            <h2 className="text-3xl font-serif font-bold text-gray-900">
              <AutoTranslate>Kristna Läran</AutoTranslate>
            </h2>
          </div>

          {/* Tabs */}
          <div className={`flex flex-wrap gap-2 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {doctrineTabs.map((tab) => (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'outline'}
                onClick={() => setActiveTab(tab.id)}
                className={activeTab === tab.id ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                <AutoTranslate>{tab.label}</AutoTranslate>
              </Button>
            ))}
          </div>

          {/* Tab Content */}
          <Card className="border-0 shadow-xl card-hover">
            <CardContent className="p-6">
              {activeTab === 'intro' && (
                <div className="prose prose-lg max-w-none">
                  <h3 className="text-2xl font-serif font-bold text-gray-900 mb-6">
                    <AutoTranslate>Inledning</AutoTranslate>
                  </h3>
                  
                  <div className="mb-8">
                    <h4 className="text-lg font-semibold text-green-700 mb-3">
                      <AutoTranslate>Det kristna namnet</AutoTranslate>
                    </h4>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>1.</strong> <AutoTranslate>Är Du kristen? — Jag är kristen genom Guds nåd.</AutoTranslate></p>
                      <p><strong>2.</strong> <AutoTranslate>Vad betyder kristen? — Kristen betyder Kristi lärjunge.</AutoTranslate></p>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h4 className="text-lg font-semibold text-green-700 mb-3">
                      <AutoTranslate>Det kristna tecknet</AutoTranslate>
                    </h4>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>3.</strong> <AutoTranslate>Vilket är de kristnas tecken? — Det Heliga Korset är de kristnas tecken.</AutoTranslate></p>
                      <p><strong>4.</strong> <AutoTranslate>Varför är det Heliga Korset de kristnas tecken? — Det Heliga Korset är de kristnas tecken därför att Jesus Kristus dog på korset för att frälsa människorna.</AutoTranslate></p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-green-700 mb-3">
                      <AutoTranslate>Den kristna läran</AutoTranslate>
                    </h4>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>5.</strong> <AutoTranslate>Vad bör den kristne veta? — Den kristne bör känna till den kristna läran.</AutoTranslate></p>
                      <p><strong>6.</strong> <AutoTranslate>Vad är den kristna läran? — Den kristna läran är den lära som vår Herre Jesus Kristus predikade för att lära oss vägen till himlen.</AutoTranslate></p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'part1' && (
                <div className="prose prose-lg max-w-none">
                  <h3 className="text-2xl font-serif font-bold text-gray-900 mb-6">
                    <AutoTranslate>Första delen: Sanningar som vi tror på</AutoTranslate>
                  </h3>
                  
                  <div className="mb-8">
                    <h4 className="text-lg font-semibold text-green-700 mb-3">
                      <AutoTranslate>Gud, en och tre</AutoTranslate>
                    </h4>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>7.</strong> <AutoTranslate>Vem är Gud? — Gud är vår Fader som är i himmelen, skapare och Herre över allting.</AutoTranslate></p>
                      <p><strong>8.</strong> <AutoTranslate>Kan Gud se allting? — Gud ser allting, det förgångna, nutiden och framtiden.</AutoTranslate></p>
                      <p><strong>9.</strong> <AutoTranslate>Var finns Gud? — Gud finns i himlen, på jorden och överallt.</AutoTranslate></p>
                      <p><strong>10.</strong> <AutoTranslate>Finns det bara en Gud? — Ja, det finns bara en Gud.</AutoTranslate></p>
                      <p><strong>11.</strong> <AutoTranslate>Vem är den Heliga Treenigheten? — Den Heliga Treenigheten är Gud Fader, Guds Son och Helig Ande. Tre personer och en enda sann Gud.</AutoTranslate></p>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h4 className="text-lg font-semibold text-green-700 mb-3">
                      <AutoTranslate>Gud Skapare</AutoTranslate>
                    </h4>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>13.</strong> <AutoTranslate>Varför säger vi att Gud är Skapare? — Vi säger att Gud är Skapare därför att Han har skapat allting av ingenting.</AutoTranslate></p>
                      <p><strong>17.</strong> <AutoTranslate>Varför har Gud skapat människorna? — Gud har skapat människorna för att vi ska älska och lyda Honom på jorden och vara lyckliga med Honom i himlen.</AutoTranslate></p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-green-700 mb-3">
                      <AutoTranslate>Gud Frälsare</AutoTranslate>
                    </h4>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>21.</strong> <AutoTranslate>Varför blev Guds Son människa? — Guds Son blev människa för att befria oss från synden och visa oss hur vi ska leva.</AutoTranslate></p>
                      <p><strong>22.</strong> <AutoTranslate>Vem är Jesus Kristus? — Jesus Kristus är Guds Son, som blev människa och föddes av jungfru Maria.</AutoTranslate></p>
                      <p><strong>26.</strong> <AutoTranslate>Var finns Jesus Kristus nu? — Jesus Kristus finns nu i himmelens härlighet och i det Allra Heligaste Sakramentet på altaret.</AutoTranslate></p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'part2' && (
                <div className="prose prose-lg max-w-none">
                  <h3 className="text-2xl font-serif font-bold text-gray-900 mb-6">
                    <AutoTranslate>Andra delen: Guds bud som vi håller</AutoTranslate>
                  </h3>
                  
                  <div className="mb-8">
                    <h4 className="text-lg font-semibold text-green-700 mb-3">
                      <AutoTranslate>Tio Guds Bud</AutoTranslate>
                    </h4>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>35.</strong> <AutoTranslate>Vilket är det första budet? — Älska Gud mer än allt annat!</AutoTranslate></p>
                      <p><strong>36.</strong> <AutoTranslate>Vem älskar Gud mer än allt annat? — Den som håller Guds bud älskar Gud mer än allt annat.</AutoTranslate></p>
                      <p><strong>42.</strong> <AutoTranslate>Vilket är det fjärde budet? — Hedra din fader och din moder.</AutoTranslate></p>
                      <p><strong>45.</strong> <AutoTranslate>Vilket är det femte budet? — Du ska icke dräpa!</AutoTranslate></p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-green-700 mb-3">
                      <AutoTranslate>Synderna</AutoTranslate>
                    </h4>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>65.</strong> <AutoTranslate>Vad är synd? — Synd är frivillig olydnad mot Gud.</AutoTranslate></p>
                      <p><strong>66.</strong> <AutoTranslate>Hurdan kan synden vara? — Synden kan vara dödssynd eller mindre synd.</AutoTranslate></p>
                      <p><strong>68.</strong> <AutoTranslate>Vilka är själens fiender? — Själens fiender är tre: världen, djävulen, köttet.</AutoTranslate></p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'part3' && (
                <div className="prose prose-lg max-w-none">
                  <h3 className="text-2xl font-serif font-bold text-gray-900 mb-6">
                    <AutoTranslate>Tredje delen: Den kristna helgelsen</AutoTranslate>
                  </h3>
                  
                  <div className="mb-8">
                    <h4 className="text-lg font-semibold text-green-700 mb-3">
                      <AutoTranslate>Guds Nåd</AutoTranslate>
                    </h4>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>69.</strong> <AutoTranslate>Vad är Nåden? — Nåden är en övernaturlig gåva, som Gud ger till oss för att vi ska undfå det eviga livet.</AutoTranslate></p>
                      <p><strong>71.</strong> <AutoTranslate>På vilka sätt kan vi få Nåden? — De viktigaste sätten att undfå nåden är att bruka sakramenten och bönen.</AutoTranslate></p>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h4 className="text-lg font-semibold text-green-700 mb-3">
                      <AutoTranslate>Bönen</AutoTranslate>
                    </h4>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>74.</strong> <AutoTranslate>Vad är att bedja? — Att bedja är att samtala med Gud, vår himmelske Fader, att lovsjunga Honom, tacka Honom och bedja om allt gott.</AutoTranslate></p>
                      <p><strong>75.</strong> <AutoTranslate>Vilken är den viktigaste bönen? — Den viktigaste bönen är &quot;Fader Vår&quot;, som vår Herre Jesus Kristus själv lärde oss.</AutoTranslate></p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-green-700 mb-3">
                      <AutoTranslate>Sakramenten</AutoTranslate>
                    </h4>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>78.</strong> <AutoTranslate>Vad är sakrament? — Sakrament är ett synligt tecken, inrättat av Jesus Kristus för att förmedla Guds Nåd till oss.</AutoTranslate></p>
                      <p><strong>79.</strong> <AutoTranslate>Vilket är det första och mest nödvändiga sakramentet? — Det första och mest nödvändiga sakramentet är DOPET.</AutoTranslate></p>
                      <p><strong>89.</strong> <AutoTranslate>Vad är EUKARISTIN? — Eukaristin är Kristi Kropps och Blods sakrament i brödets och vinets form.</AutoTranslate></p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default KristnaLaranPage;
